<script setup lang="ts">
  import { logo, logoWhite, Switch } from '@gat-ship/vue-components'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger
  } from '@gat-ship/vue-components'
  import { computed, nextTick, ref, toRaw, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import { useRouteQuery } from '@vueuse/router'
  import { useAppState } from '@gat-ship/app-state'
  import { useSupportSessionAssignedContext } from '@gat-ship/support'
  import { useUser } from '@gat-ship/user'
  import {
    navigatePreservingBrowsingContext,
    useAppShellSectionMenu,
    useSidebar,
    APP_SHELL_SECTION_OUTLET_ID,
    Badge,
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenuButton,
    Avatar,
    AvatarImage,
    AvatarFallback
  } from '@gat-ship/vue-components'
  import { accessCodes } from '@gat-ship/types'
  import { globalUtils } from '@gat-ship/utils'
  import {
    IconAdministration,
    IconSupport,
    IconChevronLeft,
    IconChevronRight,
    IconClose,
    IconCheck,
    IconDatabase,
    IconHelp,
    IconInfo,
    IconLogOut,
    IconRefresh,
    IconThemeAuto,
    IconUserSettings
  } from '@gat-ship/icons'

  const theme = globalUtils.composables.useTheme()

  // The settings + administration modals now render through the central modal
  // host (AppContexts) and open themselves from their route query
  // (`?settings=<tab>` / `?admin=<panel>`). Writing the query is all it takes to
  // open them on a specific tab/panel; the host owns mounting and z-index.
  const settingsQuery = useRouteQuery<string | null>('settings', null)

  const { isMobile, openMobile, setOpenMobile, state, toggleSidebar } =
    useSidebar()

  const route = useRoute()

  // Mobile Sheet panel swap: when a section layout registered a menu
  // (teleported into the outlet below), the Sheet shows a toggle between
  // the main navigation and the section menu. Opening the Sheet inside a
  // section defaults to the section panel — that's where the user is.
  const sectionMenu = useAppShellSectionMenu(null)
  const activePanel = ref<'navigation' | 'section'>('navigation')
  watch(openMobile, (open) => {
    if (open) {
      activePanel.value = sectionMenu?.value ? 'section' : 'navigation'
    }
  })

  // Navigating from inside the mobile Sheet closes it — unless the
  // navigation brought a *new* section menu with it (e.g. tapping
  // Administration): then the Sheet stays open on the section panel so
  // the user can pick a sub-item. nextTick lets the target layout mount
  // and register before we compare; toRaw mirrors the layouts' identity
  // guard.
  let sectionBeforeNav = sectionMenu ? toRaw(sectionMenu.value) : null
  watch(
    () => route.fullPath,
    async () => {
      await nextTick()
      const current = sectionMenu ? toRaw(sectionMenu.value) : null
      const enteredSection = current !== null && current !== sectionBeforeNav
      sectionBeforeNav = current
      if (!isMobile.value) return
      if (enteredSection) {
        activePanel.value = 'section'
      } else {
        setOpenMobile(false)
      }
    }
  )
  const showSectionPanel = computed(
    () =>
      isMobile.value && !!sectionMenu?.value && activePanel.value === 'section'
  )

  const user = useUser()
  const {
    databases: availableDatabases,
    switchDatabase,
    cacheInvalidation,
    userProfile,
    isTenantAdmin,
    isSupportUser,
    hasPermission,
    userPreferences
  } = user
  const { currentBuild } = useAppState()

  const clearCacheText = computed(() => {
    if (cacheInvalidation.isCooldownActive.value) {
      return `Clear cache (${cacheInvalidation.remainingCooldownSeconds.value}s)`
    }
    return 'Clear cache'
  })

  async function handleClearCache() {
    try {
      await cacheInvalidation.invalidateCache()
    } catch (error) {
      console.error('Failed to clear cache:', error)
    }
  }

  const userProfileImageUrl = computed(() => {
    return (
      userProfile.value?.actor?.imageUrl ?? userProfile.value?.profileImageUrl
    )
  })

  function getEnvironment() {
    return import.meta.env.MODE
  }

  // Provided at app root only for Gatship support users; undefined for
  // everyone else (the support menu item is hidden for them anyway).
  const supportSessionAssignedService = useSupportSessionAssignedContext()

  const assignedSessionsBadge = computed(() => {
    const count =
      supportSessionAssignedService?.assignedSessionsCount.value ?? 0
    return count > 0 ? count : undefined
  })

  const canAccessAdministration = computed(() => {
    return (
      isTenantAdmin.value ||
      hasPermission(accessCodes.system.currency.manageCurrencies) ||
      hasPermission(accessCodes.system.agency.viewAgencyStructure)
    )
  })

  function toggleTheme() {
    const isLight = userPreferences.getUserPreferences().theme === 'light'
    userPreferences.updateUserPreferences({ theme: isLight ? 'dark' : 'light' })
  }

  function openSupportSettings() {
    settingsQuery.value = 'support'
  }

  // `--sidebar` is scoped to this column so `bg-sidebar` resolves to the
  // app's sidebar surface (`--background` since the fg-main color-token
  // rework retired `--bg-white-0`). The support-session offsets keep
  // the fixed-positioned panel below the status bar, which viewport-
  // pinned elements would otherwise ignore.
  const sidebarStyle = computed(() => ({
    '--sidebar': 'var(--background)',
    ...(user.isSupportSession.value
      ? {
          top: 'var(--app-status-bar-height)',
          height: 'auto',
          bottom: '0px'
        }
      : {})
  }))
</script>
<template>
  <Sidebar collapsible="icon" :style="sidebarStyle">
    <!-- Rail toggle nub on the column edge (desktop only). -->
    <div
      v-if="!isMobile"
      class="absolute -right-1.5 top-1/2 cursor-pointer"
      @click="toggleSidebar()"
    >
      <div
        class="bg-muted flex h-6 w-3 items-center justify-center rounded-sm border dark:bg-neutral-800"
      >
        <icon-chevron-left
          class="size-4"
          :class="{ 'rotate-180': state === 'collapsed' }"
        />
      </div>
    </div>
    <SidebarHeader
      class="relative min-h-[88px] flex-row items-center gap-0 p-3 group-data-[collapsible=icon]:p-1"
    >
      <!-- Mobile Sheet close: styled like the Modal close button — a
      circle centered on the cross-section of the header's bottom
      border and the sheet's edge (the vendored sidebar hides the
      Sheet's stock close button). -->
      <button
        v-if="isMobile"
        type="button"
        aria-label="Close menu"
        class="bg-background shadow-xs absolute -bottom-4 -right-4 z-10 flex size-8 items-center justify-center rounded-full border hover:cursor-pointer"
        @click="setOpenMobile(false)"
      >
        <icon-close class="size-4" />
      </button>
      <!-- In the mobile Sheet, a registered section menu replaces the
      brand block with the panel toggle. -->
      <div
        v-if="isMobile && sectionMenu"
        class="bg-muted grid min-w-0 flex-1 grid-cols-2 gap-1 rounded-md p-1"
      >
        <button
          type="button"
          class="truncate rounded-sm py-1.5 text-sm"
          :class="
            activePanel === 'navigation'
              ? 'bg-background shadow-sm'
              : 'text-muted-foreground'
          "
          @click="activePanel = 'navigation'"
        >
          Navigation
        </button>
        <button
          type="button"
          class="truncate rounded-sm py-1.5 text-sm"
          :class="
            activePanel === 'section'
              ? 'bg-background shadow-sm'
              : 'text-muted-foreground'
          "
          @click="activePanel = 'section'"
        >
          {{ sectionMenu.label }}
        </button>
      </div>
      <button
        v-else
        type="button"
        class="flex w-full items-center gap-3 whitespace-nowrap p-3 text-left outline-none focus:outline-none"
      >
        <img v-if="theme.isDark.value" :src="logoWhite" class="size-10" />
        <img v-else :src="logo" class="size-10" />

        <div
          class="flex w-full items-center gap-3 group-data-[collapsible=icon]:hidden"
        >
          <div class="flex-1 space-y-1">
            <div class="text-base leading-tight">Gatship</div>
            <div class="text-muted-foreground text-xs capitalize leading-none">
              {{ getEnvironment() }}
            </div>
          </div>
        </div>
      </button>
    </SidebarHeader>
    <div class="px-5">
      <div
        role="separator"
        class="before:bg-border relative flex h-0 w-full items-center before:absolute before:left-0 before:top-1/2 before:h-px before:w-full before:-translate-y-1/2"
      ></div>
    </div>
    <SidebarContent
      v-show="!showSectionPanel"
      class="items-start gap-1.5 overflow-auto px-5 pb-4 pt-5 group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-0"
    >
      <slot name="main-menu-content" />
      <div
        class="mt-auto flex w-full flex-col gap-0.5 group-data-[collapsible=icon]:w-fit group-data-[collapsible=icon]:items-center"
      >
        <router-link
          v-if="canAccessAdministration"
          v-slot="{ href, navigate, isActive, isExactActive }"
          custom
          to="/admin/tenant-users"
        >
          <!-- In the mobile Sheet the active section's nav item is
          redundant — its menu is the other toggle panel. -->
          <SidebarMenuButton
            v-if="!(isMobile && sectionMenu && isActive)"
            as="a"
            :href="href"
            variant="nav"
            tooltip="Administration"
            :is-active="isActive"
            :data-exact-active="isExactActive"
            @click="navigatePreservingBrowsingContext($event, href, navigate)"
            @auxclick="
              navigatePreservingBrowsingContext($event, href, navigate)
            "
          >
            <icon-administration class="lucide-icon shrink-0" />
            <div
              class="flex grow items-center gap-2 group-data-[collapsible=icon]:hidden"
            >
              <div class="flex-1 text-sm">Administration</div>
              <icon-chevron-right
                v-if="isActive"
                class="lucide-icon size-4 shrink-0"
              />
            </div>
          </SidebarMenuButton>
        </router-link>
        <!-- Gatship support users only — the route itself is also
        registered conditionally during session bootstrap. -->
        <router-link
          v-if="isSupportUser"
          v-slot="{ href, navigate, isActive, isExactActive }"
          custom
          to="/support/assigned"
        >
          <SidebarMenuButton
            as="a"
            :href="href"
            variant="nav"
            tooltip="Support"
            :is-active="isActive"
            :data-exact-active="isExactActive"
            @click="navigatePreservingBrowsingContext($event, href, navigate)"
            @auxclick="
              navigatePreservingBrowsingContext($event, href, navigate)
            "
          >
            <icon-support class="lucide-icon shrink-0" />
            <!-- Icon-rail mode shows the count as an overlay pill. -->
            <Badge
              v-if="assignedSessionsBadge !== undefined"
              class="bg-muted text-foreground absolute -right-1.5 -top-1 hidden h-4 min-w-4 justify-center rounded-full px-1 text-[10px] tabular-nums group-data-[collapsible=icon]:flex"
            >
              {{ assignedSessionsBadge }}
            </Badge>
            <div
              class="flex grow items-center gap-2 group-data-[collapsible=icon]:hidden"
            >
              <div class="flex-1 text-sm">Support</div>
              <Badge
                v-if="assignedSessionsBadge !== undefined"
                class="bg-muted text-foreground h-5 min-w-5 justify-center rounded-full px-1.5 text-xs tabular-nums"
              >
                {{ assignedSessionsBadge }}
              </Badge>
              <icon-chevron-right
                v-if="isActive"
                class="lucide-icon size-4 shrink-0"
              />
            </div>
          </SidebarMenuButton>
        </router-link>
      </div>
    </SidebarContent>
    <!-- Mobile Sheet only: section layouts (admin, mail) teleport their
    sidebar content here; the toggle above swaps it with the main
    navigation. v-show (not v-if) so the teleported content survives
    panel swaps. The target only exists while the Sheet is open (its
    content is Presence-gated), so teleports into it must be v-if-gated
    on `openMobile` and use `defer`. -->
    <div
      v-if="isMobile"
      v-show="showSectionPanel"
      :id="APP_SHELL_SECTION_OUTLET_ID"
      class="flex min-h-0 grow flex-col"
    />

    <div class="px-5">
      <div
        role="separator"
        class="before:bg-border relative flex h-0 w-full items-center before:absolute before:left-0 before:top-1/2 before:h-px before:w-full before:-translate-y-1/2"
      ></div>
    </div>
    <SidebarFooter class="items-center justify-center overflow-hidden p-3">
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <button
            type="button"
            class="hover:bg-accent/50 flex w-full items-center gap-3 whitespace-nowrap rounded-sm p-3 text-left outline-none transition hover:cursor-pointer focus:outline-none group-data-[collapsible=icon]:w-fit group-data-[collapsible=icon]:p-0"
          >
            <div
              class="relative flex size-10 shrink-0 select-none items-center justify-center rounded-full text-center uppercase group-data-[collapsible=icon]:size-8"
              :class="{
                'border-support-session border-2': user.isSupportSession.value
              }"
            >
              <avatar>
                <AvatarImage
                  v-if="userProfileImageUrl"
                  class="size-full rounded-full object-cover"
                  :src="userProfileImageUrl"
                />
                <AvatarFallback>{{
                  user.userProfile.value?.fullName.slice(0, 2)
                }}</AvatarFallback>
              </avatar>
              <img
                v-if="user.isSupportSession.value"
                class="absolute bottom-1 right-1 size-4 rounded-full object-cover opacity-80"
                :src="user.userProfile.value?.profileImageUrl"
                alt=""
              />
            </div>
            <div
              class="flex w-full items-center gap-3 overflow-hidden group-data-[collapsible=icon]:hidden"
            >
              <div class="flex w-full grow flex-col space-y-1 overflow-hidden">
                <div class="flex items-center gap-0.5 truncate text-sm">
                  {{ user.userProfile.value?.fullName }}
                </div>
                <div
                  class="text-muted-foreground flex w-full items-center gap-0 overflow-hidden truncate text-xs"
                >
                  <div class="truncate">
                    {{ user.userProfile.value?.email.split('@')[0] }}
                  </div>
                  <span>
                    @{{ user.userProfile.value?.email.split('@')[1] }}
                  </span>
                </div>
              </div>
              <div
                class="flex size-6 shrink-0 items-center justify-center rounded-md"
              >
                <icon-chevron-right class="text-muted-foreground size-4" />
              </div>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          :align="isMobile ? 'center' : 'end'"
          :side="isMobile ? 'top' : 'right'"
          :side-offset="isMobile ? 8 : 24"
          class="flex w-72 flex-col gap-1 p-2"
        >
          <DropdownMenuItem
            class="text-secondary-foreground cursor-pointer p-2 capitalize"
            @select.prevent="toggleTheme()"
          >
            <icon-theme-auto class="text-secondary-foreground size-4" />
            Dark Mode
            <Switch
              class="pointer-events-none ml-auto"
              :model-value="theme.isDark.value"
            />
          </DropdownMenuItem>
          <DropdownMenuItem
            :disabled="cacheInvalidation.isCooldownActive.value"
            class="text-secondary-foreground cursor-pointer p-2 capitalize"
            @select.prevent="handleClearCache()"
          >
            <icon-refresh class="text-secondary-foreground size-4" />
            <span>{{ clearCacheText }}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            class="text-secondary-foreground cursor-pointer p-2 capitalize"
            @select="settingsQuery = 'general'"
          >
            <icon-user-settings class="text-secondary-foreground size-4" />
            Settings
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              class="text-secondary-foreground cursor-pointer gap-2 p-2 capitalize"
            >
              <icon-database class="text-secondary-foreground size-4" />
              <div class="flex grow flex-col text-left">
                <span class="leading-tight">Switch Database</span>
                <span class="text-muted-foreground text-xs leading-none">{{
                  availableDatabases.current?.name
                }}</span>
              </div>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent class="flex flex-col gap-1 p-2">
              <DropdownMenuItem
                v-for="db in availableDatabases.all"
                :key="db.id"
                :disabled="db.id === availableDatabases.current?.id"
                class="text-secondary-foreground min-h-10 cursor-pointer p-2 capitalize"
                @select="switchDatabase(db.id)"
              >
                <icon-database class="text-secondary-foreground size-4" />
                <div class="flex grow flex-col text-left">
                  <span class="leading-tight">{{ db.name }}</span>
                </div>
                <icon-check
                  v-if="db.id === availableDatabases.current?.id"
                  class="text-secondary-foreground size-4"
                />
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              class="text-secondary-foreground cursor-pointer gap-2 p-2 capitalize"
            >
              <icon-help class="text-secondary-foreground size-4" />
              <span>Help</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent class="flex flex-col gap-1 p-2">
              <DropdownMenuItem
                class="text-secondary-foreground cursor-pointer p-2 capitalize"
                @select="openSupportSettings()"
              >
                <icon-support class="text-secondary-foreground size-4" />
                Support
              </DropdownMenuItem>
              <DropdownMenuItem
                as-child
                class="text-secondary-foreground cursor-pointer p-2 capitalize"
              >
                <a
                  href="https://gatship.com/about-us/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <icon-info class="text-secondary-foreground size-4" />
                  About Gatship
                </a>
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            class="text-secondary-foreground cursor-pointer p-2 capitalize"
            @select="$auth0.logout()"
          >
            <icon-log-out class="text-secondary-foreground size-4" />
            Logout
          </DropdownMenuItem>
          <div class="text-xs">
            Build: {{ currentBuild }} ({{ getEnvironment() }})
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarFooter>
  </Sidebar>
</template>
